import { useRef, useState } from 'react';
import { fr } from 'date-fns/locale';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
  accountStatementErrorText,
  accountStatementFileName,
  startOfCurrentYear,
  startOfToday,
  toIsoDay,
} from 'components/accountStatement/accountStatementFile';
import Button from 'components/button';
import { downloadFile } from 'components/invoiceList/useInvoiceExport';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

registerLocale('fr-FR', fr);

/**
 * The PDF account statement of a company over a chosen period, built by the
 * api from its customer account at Pennylane. `modalData` is
 * `{ idCompany, companyName }`; the name only names the file.
 *
 * A refusal stays in the modal with the text of its code; a success downloads
 * the file and closes the modal.
 */
const ModalAccountStatement = () => {
  const { toggle } = useModal();
  const { language, modalData } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`accountStatement.${key}`, fallback);
  const [from, setFrom] = useState(startOfCurrentYear);
  const [to, setTo] = useState(startOfToday);
  const [isPending, setIsPending] = useState(false);
  const [failure, setFailure] = useState(null);
  // State lands one render late: a double click must still send one request.
  const isSending = useRef(false);

  const idCompany = modalData?.idCompany;
  if (!idCompany) return null;

  const fromDay = from ? toIsoDay(from) : null;
  const toDay = to ? toIsoDay(to) : null;
  // Same-length "YYYY-MM-DD" strings compare as dates, whatever the time of day
  // the picker put on them.
  const isPeriodValid = Boolean(fromDay && toDay) && fromDay <= toDay;
  const isDownloadDisabled = isPending || !isPeriodValid;

  const done = () => {
    isSending.current = false;
    setIsPending(false);
  };

  const download = () => {
    if (isSending.current || !isPeriodValid) return;
    isSending.current = true;
    setIsPending(true);
    setFailure(null);
    ApiDatabase.getCompanyAccountStatement(
      { idCompany, from: fromDay, to: toDay },
      (blob) => {
        done();
        downloadFile(
          blob,
          accountStatementFileName(modalData?.companyName, fromDay, toDay),
        );
        toggle();
      },
      (error) => {
        done();
        if (error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        setFailure(
          accountStatementErrorText(language, error?.response?.data?.errorCode),
        );
      },
    );
  };

  const datePickerProps = {
    dateFormat: 'dd/MM/yyyy',
    locale: 'fr-FR',
    placeholderText: text('datePlaceholder', 'jj/mm/aaaa'),
    disabled: isPending,
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: 'select',
    popperPlacement: 'bottom',
    // ModalBody scrolls: an absolute calendar would be clipped at its bottom
    // edge, under the footer.
    popperProps: { strategy: 'fixed' },
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isPending}>
        {text('title', 'Relevé de compte')}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4 text-sm">
          <p className="text-gray-700">
            {text(
              'intro',
              'Les mouvements du compte client de l’entreprise chez Pennylane sur la période, à partir du solde au premier jour.',
            )}
          </p>
          {/* One cell per picker: an open calendar adds a sibling to its
              input, which would otherwise push « Au » to the next row. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <DatePicker
                {...datePickerProps}
                selected={from}
                onChange={(date) => setFrom(date)}
                customInput={<StyledDateInput label={text('from', 'Du')} />}
              />
            </div>
            <div>
              <DatePicker
                {...datePickerProps}
                selected={to}
                onChange={(date) => setTo(date)}
                customInput={<StyledDateInput label={text('to', 'Au')} />}
              />
            </div>
          </div>
          {!isPeriodValid && (
            <div role="alert" className="text-red-600">
              {accountStatementErrorText(language, 'INVALID_PERIOD')}
            </div>
          )}
          {failure && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-4 py-3 text-red-700"
            >
              {failure}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size="LG"
          color={isPending ? 'DISABLED' : 'SECONDARY'}
          disabled={isPending}
          onClick={toggle}
        >
          {text('cancel', 'Annuler')}
        </Button>
        <Button
          size="LG"
          color={isDownloadDisabled ? 'DISABLED' : 'PRIMARY'}
          disabled={isDownloadDisabled}
          onClick={download}
        >
          {isPending
            ? text('downloading', 'Téléchargement…')
            : text('download', 'Télécharger')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalAccountStatement;
